/**
 * BOGO Cart Manager
 * 1. Independent Product Isolation: Manages each product/variant separately in cart.
 * 2. Synchronizes Paid and Free paired units 1:1 on '+' and '-'.
 * 3. Prevents '-' decrement when quantity is 1 (disabled / not-allowed).
 * 4. Removes ALL units of the specific clicked variant when Delete (trash icon) is clicked.
 * 5. Preserves Drawer state (smooth updates, keeping drawer open).
 * 6. Allows multiple BOGO and non-BOGO products to coexist in cart without interference.
 */
(function () {
  let isBogoUpdating = false;

  function getBogoData() {
    const bogoScript =
      document.getElementById('CartDrawer-BogoData') ||
      document.getElementById('Cart-BogoData') ||
      document.querySelector('[id*="BogoData"]');

    if (!bogoScript) return [];

    try {
      return JSON.parse(bogoScript.textContent || '[]');
    } catch (e) {
      console.error('Error parsing BOGO cart data:', e);
      return [];
    }
  }

  function updateMinusButtonsUI() {
    document.querySelectorAll('quantity-input').forEach((container) => {
      const input = container.querySelector('input.quantity__input');
      const minusBtn = container.querySelector('.quantity__button[name="minus"], button[name="minus"]');
      if (input && minusBtn) {
        const val = parseInt(input.value, 10) || 1;
        const minVal = parseInt(input.dataset.min || input.min || '1', 10);
        minusBtn.classList.toggle('disabled', val <= minVal);
      }
    });
  }

  let liveDrawerItemsScroll = 0;
  let liveDrawerInnerScroll = 0;

  document.addEventListener(
    'scroll',
    (e) => {
      const target = e.target;
      if (!target) return;
      if (target.tagName === 'CART-DRAWER-ITEMS' && target.scrollTop > 0) {
        liveDrawerItemsScroll = target.scrollTop;
      }
      if (target.classList && target.classList.contains('drawer__inner') && target.scrollTop > 0) {
        liveDrawerInnerScroll = target.scrollTop;
      }
    },
    true
  );

  function captureScrollPositions() {
    const drawerItems = document.querySelector('cart-drawer-items');
    const drawerInner = document.querySelector('#CartDrawer .drawer__inner') || document.querySelector('.drawer__inner');
    if (drawerItems && drawerItems.scrollTop > 0) liveDrawerItemsScroll = drawerItems.scrollTop;
    if (drawerInner && drawerInner.scrollTop > 0) liveDrawerInnerScroll = drawerInner.scrollTop;

    const scrollContainers = [
      drawerInner,
      drawerItems,
      document.querySelector('#CartDrawer-CartItems'),
      document.querySelector('.drawer__cart-items-wrapper'),
      document.querySelector('#CartDrawer'),
      document.querySelector('cart-drawer'),
      document.documentElement,
      document.body,
    ].filter(Boolean);

    return scrollContainers.map((el) => ({
      el,
      top: el.scrollTop,
      left: el.scrollLeft,
    }));
  }

  function restoreCapturedScrollPositions(savedList, targetVariantId) {
    const apply = () => {
      const drawerItems = document.querySelector('cart-drawer-items');
      const drawerInner = document.querySelector('#CartDrawer .drawer__inner') || document.querySelector('.drawer__inner');

      if (drawerItems && liveDrawerItemsScroll > 0) {
        drawerItems.scrollTop = liveDrawerItemsScroll;
      }
      if (drawerInner && liveDrawerInnerScroll > 0) {
        drawerInner.scrollTop = liveDrawerInnerScroll;
      }

      if (Array.isArray(savedList)) {
        savedList.forEach(({ el, top, left }) => {
          if (el && top > 0) el.scrollTop = top;
          if (el && left > 0) el.scrollLeft = left;
        });
      }

      if (targetVariantId) {
        const itemEl =
          document.querySelector(`[data-quantity-variant-id="${targetVariantId}"]`) ||
          document.querySelector(`[data-variant-id="${targetVariantId}"]`);
        const row = itemEl?.closest('tr, .cart-item');
        if (row && typeof row.scrollIntoView === 'function') {
          row.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        }
      }
    };

    apply();
    requestAnimationFrame(apply);
    setTimeout(apply, 10);
    setTimeout(apply, 40);
    setTimeout(apply, 100);
    setTimeout(apply, 200);
    setTimeout(apply, 350);
  }

  function applyCartState(parsedState, savedScroll, targetVariantId) {
    const itemCount = typeof parsedState.item_count !== 'undefined' ? parsedState.item_count : 0;
    const isEmpty = itemCount === 0;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.toggle('is-empty', isEmpty);
    }

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) {
      cartFooter.classList.toggle('is-empty', isEmpty);
    }

    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      cartDrawerItems.classList.toggle('is-empty', isEmpty);
    }

    const cartItemsElement = document.querySelector('cart-items');
    if (cartItemsElement) {
      cartItemsElement.classList.toggle('is-empty', isEmpty);
    }

    if (parsedState && parsedState.sections) {
      // 1. Cart Drawer
      if (parsedState.sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
        const targetDrawerInner =
          document.querySelector('#CartDrawer .drawer__inner') || document.querySelector('.drawer__inner');

        if (isEmpty) {
          const sourceDrawerInner =
            doc.querySelector('#CartDrawer .drawer__inner') || doc.querySelector('.drawer__inner');
          if (targetDrawerInner && sourceDrawerInner) {
            targetDrawerInner.innerHTML = sourceDrawerInner.innerHTML;
            targetDrawerInner.className = sourceDrawerInner.className;
          }
        } else {
          // Update contents precisely without destroying the scroll container DOM elements
          const targetItems = document.querySelector('#CartDrawer-CartItems');
          const sourceItems = doc.querySelector('#CartDrawer-CartItems');
          if (targetItems && sourceItems) {
            const currentHeight = targetItems.offsetHeight;
            if (currentHeight > 0) {
              targetItems.style.minHeight = currentHeight + 'px';
            }
            targetItems.innerHTML = sourceItems.innerHTML;
            setTimeout(() => {
              targetItems.style.minHeight = '';
            }, 350);
          }

          const targetFooter = document.querySelector('.cart-drawer__footer');
          const sourceFooter = doc.querySelector('.cart-drawer__footer');
          if (targetFooter && sourceFooter) {
            targetFooter.innerHTML = sourceFooter.innerHTML;
          }

          const targetCtas = document.querySelector('.cart__ctas');
          const sourceCtas = doc.querySelector('.cart__ctas');
          if (targetCtas && sourceCtas) {
            targetCtas.innerHTML = sourceCtas.innerHTML;
          }
        }

        restoreCapturedScrollPositions(savedScroll, targetVariantId);

        const overlay = document.querySelector('#CartDrawer-Overlay');
        if (overlay && cartDrawer && typeof cartDrawer.close === 'function') {
          overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
        }
      }

      // 2. Main Cart Items
      if (parsedState.sections['main-cart-items']) {
        const doc = new DOMParser().parseFromString(parsedState.sections['main-cart-items'], 'text/html');
        const targetMain =
          document.querySelector('#main-cart-items .js-contents') || document.querySelector('cart-items');
        const sourceMain = doc.querySelector('#main-cart-items .js-contents') || doc.querySelector('cart-items');
        if (targetMain && sourceMain) {
          targetMain.innerHTML = sourceMain.innerHTML;
        }
      }

      // 3. Main Cart Footer
      if (parsedState.sections['main-cart-footer']) {
        const doc = new DOMParser().parseFromString(parsedState.sections['main-cart-footer'], 'text/html');
        const targetFooter = document.querySelector('#main-cart-footer .js-contents');
        const sourceFooter = doc.querySelector('#main-cart-footer .js-contents');
        if (targetFooter && sourceFooter) {
          targetFooter.innerHTML = sourceFooter.innerHTML;
        }
      }

      // 4. Cart Icon Bubble
      if (parsedState.sections['cart-icon-bubble']) {
        const doc = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
        const bubbleContainer = document.querySelector('#cart-icon-bubble');
        const newBubble = doc.querySelector('#cart-icon-bubble') || doc.querySelector('.shopify-section');
        if (bubbleContainer && newBubble) {
          bubbleContainer.innerHTML = newBubble.innerHTML;
        }
      }
    }

    updateMinusButtonsUI();
  }

  async function sendCartUpdate(updates, savedScroll, targetVariantId) {
    if (!updates || Object.keys(updates).length === 0) return;
    if (isBogoUpdating) return;

    isBogoUpdating = true;

    try {
      const sectionsToRender = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];

      const response = await fetch(`${window.routes?.cart_update_url || '/cart/update.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          updates: updates,
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const parsedState = await response.json();
      applyCartState(parsedState, savedScroll, targetVariantId);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bogo-sync', cartData: parsedState });
      }
    } catch (error) {
      console.error('Error updating BOGO cart:', error);
    } finally {
      isBogoUpdating = false;
    }
  }

  // Intercept '+', '-', and Delete (trash) clicks for BOGO items only
  document.addEventListener(
    'click',
    async function (event) {
      const qtyBtn = event.target.closest(
        '.quantity__button[name="minus"], .quantity__button[name="plus"], button[name="minus"], button[name="plus"]'
      );
      const removeBtn = event.target.closest(
        'cart-remove-button, .cart-remove-button, [id^="CartDrawer-Remove-"], [id^="Remove-"]'
      );

      if (!qtyBtn && !removeBtn) return;

      const cartItems = getBogoData();
      if (!Array.isArray(cartItems) || cartItems.length === 0) return;

      const btn = qtyBtn || removeBtn;
      const input = btn
        .closest('quantity-input, .cart-item__quantity-wrapper, .cart-item, tr')
        ?.querySelector('input.quantity__input');

      const lineIndexAttr =
        input?.dataset?.index ||
        btn.dataset?.index ||
        btn.getAttribute('data-index') ||
        btn.closest('[data-index]')?.getAttribute('data-index');

      let clickedItem = null;
      if (lineIndexAttr) {
        const lineIdx = parseInt(lineIndexAttr, 10);
        clickedItem = cartItems.find((item, idx) => (item.line_index || idx + 1) === lineIdx);
      }

      if (!clickedItem) {
        const variantId =
          input?.dataset?.quantityVariantId ||
          btn.dataset?.variantId ||
          btn.closest('[data-variant-id]')?.dataset?.variantId ||
          btn.closest('tr, .cart-item')?.querySelector('[data-quantity-variant-id]')?.dataset?.quantityVariantId;
        if (variantId) {
          clickedItem = cartItems.find(
            (item) => String(item.variant_id) === String(variantId) || String(item.id) === String(variantId)
          );
        }
      }

      // If clicked item is a BOGO product, handle custom paired synchronization
      if (clickedItem && clickedItem.is_bogo) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const currentVariantId = String(clickedItem.variant_id || clickedItem.id);
        const savedScroll = captureScrollPositions();
        const variantItems = cartItems.filter(
          (item) => String(item.variant_id || item.id) === currentVariantId
        );

        const currentQty = parseInt(input?.value || clickedItem.quantity, 10) || 1;
        const updates = {};

        if (removeBtn) {
          // DELETE: Remove all units for THIS specific product variant only
          variantItems.forEach((item) => {
            updates[item.key || item.id] = 0;
          });
        } else if (qtyBtn) {
          const btnName = qtyBtn.getAttribute('name');
          const isPlus = btnName === 'plus';

          if (!isPlus && currentQty <= 1) {
            // When quantity is 1, minus is disabled / not allowed
            return;
          }

          let targetQty = isPlus ? currentQty + 1 : currentQty - 1;
          if (targetQty < 1) targetQty = 1;

          const paidItems = variantItems.filter((item) => !item.has_bogo_prop);
          const freeItems = variantItems.filter((item) => item.has_bogo_prop);

          const primaryPaid = paidItems[0];
          const primaryFree = freeItems[0];

          // Zero out any duplicate lines so only 1 paid and 1 free remain
          paidItems.slice(1).forEach((item) => {
            updates[item.key || item.id] = 0;
          });
          freeItems.slice(1).forEach((item) => {
            updates[item.key || item.id] = 0;
          });

          if (primaryPaid) {
            updates[primaryPaid.key || primaryPaid.id] = targetQty;
          }
          if (primaryFree) {
            updates[primaryFree.key || primaryFree.id] = targetQty;
          }
        }

        if (Object.keys(updates).length > 0) {
          await sendCartUpdate(updates, savedScroll, currentVariantId);
        }
      }
    },
    true // Capture phase
  );

  async function consolidateBogoCart() {
    if (isBogoUpdating) return;
    const cartItems = getBogoData();
    if (!Array.isArray(cartItems) || cartItems.length === 0) return;

    const updates = {};
    let needsUpdate = false;

    // Group items by variant_id
    const variantMap = {};
    cartItems.forEach((item) => {
      if (item.is_bogo) {
        const vid = String(item.variant_id || item.id);
        if (!variantMap[vid]) variantMap[vid] = [];
        variantMap[vid].push(item);
      }
    });

    Object.keys(variantMap).forEach((vid) => {
      const items = variantMap[vid];
      const paidItems = items.filter((i) => !i.has_bogo_prop);
      const freeItems = items.filter((i) => i.has_bogo_prop);

      if (
        paidItems.length > 1 ||
        freeItems.length > 1 ||
        (paidItems.length === 1 && freeItems.length === 1 && paidItems[0].quantity !== freeItems[0].quantity)
      ) {
        needsUpdate = true;
        const targetQty = paidItems[0]?.quantity || freeItems[0]?.quantity || 1;

        if (paidItems[0]) updates[paidItems[0].key || paidItems[0].id] = targetQty;
        if (freeItems[0]) updates[freeItems[0].key || freeItems[0].id] = targetQty;

        paidItems.slice(1).forEach((i) => {
          updates[i.key || i.id] = 0;
        });
        freeItems.slice(1).forEach((i) => {
          updates[i.key || i.id] = 0;
        });
      }
    });

    if (needsUpdate && Object.keys(updates).length > 0) {
      const savedScroll = captureScrollPositions();
      await sendCartUpdate(updates, savedScroll);
    }
  }

  // Initialize UI state on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      updateMinusButtonsUI();
      consolidateBogoCart();
    });
  } else {
    updateMinusButtonsUI();
    consolidateBogoCart();
  }

  // Update UI on cart events
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'bogo-sync') return;
      setTimeout(() => {
        updateMinusButtonsUI();
        consolidateBogoCart();
      }, 100);
    });
  }
})();
